/**
 * What the app believes about the session at boot, given what the server said.
 *
 * Extracted from `main.tsx` because the interesting part is a decision table, and a decision
 * table about who is signed in is exactly what should not live only in a React effect.
 *
 * The session is still the httpOnly cookie, and the server is still the only authority on it
 * (ADR-0008). What this adds is a **hint** — the last account confirmed on this device — so a
 * boot with no signal can open as that account instead of as nobody. It is provisional: it is
 * re-checked as soon as the server can be reached, and a clean 401 outranks it.
 */
import type { AuthUser } from "@sportsos/engine";
import type { Session } from "../screen.ts";

/** What asking the server produced. Exactly three outcomes, and they mean different things. */
export type BootAnswer =
  | { readonly kind: "user"; readonly user: AuthUser }
  /** A clean 401: the server answered, and the answer is no. */
  | { readonly kind: "unauthorized" }
  /** No answer at all — no signal, a proxy, the API down. Not the same as no. */
  | { readonly kind: "unreachable" };

export interface BootSession {
  readonly session: Session;
  /** True when the session is the cached hint rather than something the server confirmed. */
  readonly provisional: boolean;
}

export function bootSession(
  answer: BootAnswer,
  hint: AuthUser | null,
  guest: boolean,
): BootSession {
  switch (answer.kind) {
    case "user":
      return { session: answer.user, provisional: false };
    case "unauthorized":
      // A clean 401 with a guest flag is somebody who chose not to have an account, not
      // somebody signed out. The hint does not rescue it: the server just said no.
      return { session: guest ? "guest" : null, provisional: false };
    case "unreachable":
      return hint === null
        ? { session: "unreachable", provisional: false }
        : { session: hint, provisional: true };
  }
}

/** What to do with the stored hint after an answer. */
export function hintAction(answer: BootAnswer): "save" | "clear" | "keep" {
  switch (answer.kind) {
    case "user":
      return "save";
    case "unauthorized":
      return "clear";
    case "unreachable":
      // Forgetting who was signed in because the network dropped is the failure this exists
      // to prevent.
      return "keep";
  }
}
